// Файл use_image_pan_zoom.js

/**
 * Файл реализует логику модуля «use image pan zoom» в разделе «DocumentAttachments» интерфейса.
 * В нём собраны связанные обработчики и вспомогательные операции для этой части приложения.
 * Код взаимодействует с соседними компонентами и передаёт им подготовленные данные без дублирования общей логики.
 * Такое разделение упрощает сопровождение, проверку поведения и дальнейшее расширение функциональности.
 */

import { useCallback, useRef, useState } from 'react';


const MIN_SCALE = 0.1;
const MAX_SCALE = 10;
const clamp_scale = (value) => Math.min(
    MAX_SCALE,
    Math.max(MIN_SCALE, value),
);
const pointer_distance = (left, right) => Math.hypot(
    right.clientX - left.clientX,
    right.clientY - left.clientY,
);


export const use_image_pan_zoom = () => {
    const canvas_ref = useRef(null);
    const image_ref = useRef(null);
    const pointers_ref = useRef(new Map());
    const gesture_ref = useRef(null);
    const [scale, set_scale] = useState(1);
    const [offset, set_offset] = useState({ x: 0, y: 0 });

    const fit_to_screen = useCallback(() => {
        const canvas = canvas_ref.current;
        const image = image_ref.current;

        if (!canvas || !image?.naturalWidth || !image?.naturalHeight) {
            return;
        }

        const horizontal_space = Math.max(canvas.clientWidth - 20, 1);
        const vertical_space = Math.max(canvas.clientHeight - 20, 1);
        const fitted_scale = Math.min(
            horizontal_space / image.naturalWidth,
            vertical_space / image.naturalHeight,
            1,
        );
        set_scale(clamp_scale(fitted_scale));
        set_offset({ x: 0, y: 0 });
    }, []);

    const reset_one_to_one = useCallback(() => {
        set_scale(1);
        set_offset({ x: 0, y: 0 });
    }, []);

    const zoom_by = useCallback((factor) => {
        set_scale((current_scale) => clamp_scale(current_scale * factor));
    }, []);

    const handle_wheel = useCallback((event) => {
        event.preventDefault();
        zoom_by(event.deltaY < 0 ? 1.12 : 1 / 1.12);
    }, [zoom_by]);

    const start_single_pointer = useCallback((pointer) => {
        gesture_ref.current = {
            mode: 'drag',
            start_x: pointer.clientX,
            start_y: pointer.clientY,
            base_x: offset.x,
            base_y: offset.y,
        };
    }, [offset]);

    const start_pinch = useCallback(() => {
        const pointers = Array.from(pointers_ref.current.values());

        if (pointers.length < 2) {
            return;
        }

        gesture_ref.current = {
            mode: 'pinch',
            distance: pointer_distance(pointers[0], pointers[1]),
            base_scale: scale,
        };
    }, [scale]);

    const handle_pointer_down = useCallback((event) => {
        event.currentTarget.setPointerCapture?.(event.pointerId);
        pointers_ref.current.set(event.pointerId, event);

        if (pointers_ref.current.size === 1) {
            start_single_pointer(event);
        } else {
            start_pinch();
        }
    }, [start_pinch, start_single_pointer]);

    const handle_pointer_move = useCallback((event) => {
        if (!pointers_ref.current.has(event.pointerId)) {
            return;
        }

        pointers_ref.current.set(event.pointerId, event);
        const gesture = gesture_ref.current;

        if (!gesture) {
            return;
        }

        if (gesture.mode === 'drag' && pointers_ref.current.size === 1) {
            set_offset({
                x: gesture.base_x + event.clientX - gesture.start_x,
                y: gesture.base_y + event.clientY - gesture.start_y,
            });
            return;
        }

        if (pointers_ref.current.size >= 2) {
            const pointers = Array.from(pointers_ref.current.values());
            const current_distance = pointer_distance(
                pointers[0],
                pointers[1],
            );
            const initial_distance = Math.max(gesture.distance || 1, 1);
            set_scale(clamp_scale(
                gesture.base_scale * current_distance / initial_distance
            ));
        }
    }, []);

    const handle_pointer_end = useCallback((event) => {
        pointers_ref.current.delete(event.pointerId);

        if (pointers_ref.current.size === 1) {
            start_single_pointer(
                Array.from(pointers_ref.current.values())[0]
            );
        } else if (pointers_ref.current.size >= 2) {
            start_pinch();
        } else {
            gesture_ref.current = null;
        }
    }, [start_pinch, start_single_pointer]);

    return {
        canvas_ref,
        image_ref,
        scale,
        offset,
        fit_to_screen,
        reset_one_to_one,
        zoom_by,
        handle_wheel,
        handle_pointer_down,
        handle_pointer_move,
        handle_pointer_end,
    };
};
